/**
 * Tenant Context
 * 
 * Provides tenant isolation for all database operations.
 * Makes I1 (Tenant isolation and identity production-safe) true.
 * 
 * Evidence: All queries are scoped by workspace; cross-tenant access is blocked
 */

import { getSupabaseClient } from './supabaseClient';
import { notifyWorkspaceContextChanged } from './workspaceContextEvents';

/**
 * Tenant/Workspace context for scoped operations
 */
export interface TenantContext {
  workspaceId: string;
  workspaceName?: string;
  userId: string;
  userEmail?: string;
  /** Display name (from auth metadata or users table); prefer over email for UI. */
  userName?: string;
}

/**
 * Current tenant context (set on app initialization)
 */
let currentTenant: TenantContext | null = null;

/**
 * Set the current tenant context
 */
export function setTenantContext(context: TenantContext): void {
  const previousWorkspaceId = currentTenant?.workspaceId ?? null;
  currentTenant = context;
  console.log('[TenantContext] Set context:', context.workspaceId);
  if (previousWorkspaceId !== context.workspaceId) {
    notifyWorkspaceContextChanged(context.workspaceId);
  }
}

/**
 * Get the current tenant context
 */
export function getTenantContext(): TenantContext | null {
  return currentTenant;
}

/**
 * Update just the workspace on the current tenant context (e.g. when the user switches workspace in the UI).
 * If no tenant context exists yet, this is a no-op.
 */
export function updateTenantContextWorkspace(workspaceId: string, workspaceName?: string): void {
  if (!currentTenant) return;
  const previousWorkspaceId = currentTenant.workspaceId;
  currentTenant = { ...currentTenant, workspaceId, workspaceName: workspaceName ?? currentTenant.workspaceName };
  console.log('[TenantContext] Workspace updated:', workspaceId);
  if (previousWorkspaceId !== workspaceId) notifyWorkspaceContextChanged(workspaceId);
}

/**
 * Clear the tenant context (on logout)
 */
export function clearTenantContext(): void {
  const hadWorkspace = currentTenant !== null;
  currentTenant = null;
  console.log('[TenantContext] Cleared');
  if (hadWorkspace) notifyWorkspaceContextChanged(null);
}

/**
 * Require tenant context (throws if not set)
 */
export function requireTenantContext(): TenantContext {
  if (!currentTenant) {
    throw new Error('Tenant context not set. User must be authenticated.');
  }
  return currentTenant;
}

/**
 * Demo workspace ID (matches DEMO_SETUP.sql)
 */
export const DEMO_WORKSPACE_ID = 'de000001-0000-0000-0000-000000000001';

/**
 * Default workspace for development (now uses demo workspace)
 */
export const DEFAULT_WORKSPACE_ID = DEMO_WORKSPACE_ID;

/**
 * Initialize tenant context from the current user.
 * @param supabaseUserId - Internal user id (users.id UUID). Must be the Supabase users.id for workspace_memberships lookup.
 */
/** Shape of the `workspace_memberships` rows read below (embedded workspace). */
interface MembershipRow {
  workspaces?: { id: string; name: string; is_demo?: boolean | null } | null;
}

export async function initializeTenantFromUser(
  supabaseUserId: string,
  email?: string,
  userName?: string,
  preferredWorkspaceId?: string | null
): Promise<TenantContext | null> {
  const supabase = await import('./supabaseClient').then(m => m.getSupabaseClient());
  
  // Look up THIS user's workspace memberships only (tenant isolation)
  const { data: memberships } = await supabase
    .from('workspace_memberships')
    .select(`
      workspace_id,
      role,
      workspaces:workspace_id (
        id,
        name,
        is_demo
      )
    `)
    .eq('user_id', supabaseUserId)
    .eq('status', 'active');
  
  // Fail honestly when the authenticated user has no active workspace membership.
  if (!memberships || memberships.length === 0) {
    console.warn('[TenantContext] No active workspace memberships found for user:', supabaseUserId);
    return null;
  }

  // The person's own selection wins while they still belong to it. With none,
  // prefer demo workspace if user has access, otherwise use first active membership.
  let workspaceId = DEFAULT_WORKSPACE_ID;
  let workspaceName = 'Demo Workspace';
  const rows = memberships as unknown as MembershipRow[];
  const preferredMembership = preferredWorkspaceId
    ? rows.find((m) => m.workspaces?.id === preferredWorkspaceId)
    : undefined;
  const demoMembership = rows.find((m) => m.workspaces?.is_demo === true);
  const selectedMembership = preferredMembership ?? demoMembership ?? rows[0];

  if (selectedMembership?.workspaces) {
    workspaceId = selectedMembership.workspaces.id;
    workspaceName = selectedMembership.workspaces.name;
  }

  // A workspace set while the memberships were loading is newer than the choice
  // above (Layout sets it from the selection). Overwriting it left the app
  // working in one workspace while the selection said another.
  const alreadySet = currentTenant?.userId === supabaseUserId ? currentTenant : null;

  const context: TenantContext = {
    workspaceId: alreadySet?.workspaceId ?? workspaceId,
    workspaceName: alreadySet?.workspaceName ?? workspaceName,
    userId: supabaseUserId,
    userEmail: email,
    userName: userName?.trim() || undefined,
  };
  
  console.log('[TenantContext] Initialized:', context.workspaceName, context.workspaceId);
  setTenantContext(context);
  return context;
}

/**
 * Ensure a workspace context exists. If one is already set, return it.
 * Otherwise attempt to initialize from the current Supabase auth session.
 * Returns null when no authenticated session is available.
 */
async function resolveUserDisplayName(
  supabaseUserId: string,
  sessionMetadata: Record<string, unknown> | undefined,
): Promise<string | undefined> {
  const supabase = getSupabaseClient();
  const { data: userRow } = await supabase
    .from('users')
    .select('name')
    .eq('id', supabaseUserId)
    .maybeSingle();
  const dbName = (userRow?.name as string | undefined)?.trim();
  if (dbName) return dbName;

  const meta = sessionMetadata ?? {};
  const fromMeta =
    meta.full_name ?? meta.name ?? meta.user_name;
  return typeof fromMeta === 'string' && fromMeta.trim() ? fromMeta.trim() : undefined;
}

export async function ensureWorkspaceContext(
  preferredWorkspaceId?: string | null,
): Promise<TenantContext | null> {
  if (currentTenant) return currentTenant;

  try {
    const supabase = getSupabaseClient();
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) return null;

    const name = await resolveUserDisplayName(
      session.user.id,
      session.user.user_metadata as Record<string, unknown> | undefined,
    );
    return initializeTenantFromUser(
      session.user.id,
      session.user.email ?? undefined,
      name,
      preferredWorkspaceId,
    );
  } catch (err) {
    console.warn('[TenantContext] ensureWorkspaceContext failed:', err);
    return null;
  }
}

/**
 * Scope filter for Supabase queries
 * Add this to all queries to enforce tenant isolation
 */
export function getScopeFilter(): { scopeKind: string; scopeId: string } {
  const tenant = requireTenantContext();
  return {
    scopeKind: 'workspace',
    scopeId: tenant.workspaceId,
  };
}

/**
 * Validate that a resource belongs to the current tenant
 */
export async function validateResourceAccess(
  table: string,
  resourceId: string,
  scopeColumn = 'workspace_scope_id'
): Promise<boolean> {
  const tenant = getTenantContext();
  if (!tenant) {
    return false;
  }

  const supabase = getSupabaseClient();
  
  const { data, error } = await supabase
    .from(table)
    .select('id')
    .eq('id', resourceId)
    .eq(scopeColumn, tenant.workspaceId)
    .single();

  if (error || !data) {
    console.warn(`[TenantContext] Access denied to ${table}/${resourceId}`);
    return false;
  }

  return true;
}

/**
 * Create a scoped insert object with workspace context
 */
export function withScope<T extends object>(data: T): T & {
  workspace_scope_kind: string;
  workspace_scope_id: string;
} {
  const tenant = requireTenantContext();
  return {
    ...data,
    workspace_scope_kind: 'workspace',
    workspace_scope_id: tenant.workspaceId,
  };
}

/**
 * HOC for tenant-scoped queries
 */
export function scopedQuery<T extends { eq(column: string, value: string): T }>(baseQuery: T): T {
  const tenant = getTenantContext();
  if (!tenant) {
    throw new Error('Cannot execute scoped query without tenant context');
  }
  
  return baseQuery.eq('workspace_scope_id', tenant.workspaceId);
}

/**
 * React hook for accessing tenant context
 * Returns a simplified interface for components
 */
export function useTenantContext(): { currentWorkspaceId: string | null } {
  const context = getTenantContext();
  
  return {
    currentWorkspaceId: context?.workspaceId ?? null,
  };
}

